import React from 'react'
import { LocaleContext } from '../../context/locale'
import config from 'config'
import { FetchErrorMessage } from 'components/fetchErrorMessage'
import Markdown, { compiler } from 'markdown-to-jsx'
import { Link as TALink } from '@ta-interaktiv/contentbricks'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import de from 'locales/de.yml'
import fr from 'locales/fr.yml'

const STRINGS = {
  de: de,
  fr: fr
}

export class Autotext extends React.Component {
  previousContext = null

  static contextType = LocaleContext
  static tenants = require('../../data/tenants.csv')

  /**
   * @param {any} props
   * @param {any} context
   */
  constructor(props, context) {
    super(props, context)
    // enhance RegExp
    /* eslint-disable */
    RegExp.escape = function(string) {
      return string.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')
    }
    /* eslint-enable */

    this.state = {
      autoText: null,
      lastModificationTime: null,
      errorMessage: null
    }

    // get initial autotext
    this.getCantonalText(this.props.kt, this.props.type, this.context)
      .then(data => {
        this.setState({
          autoText:
            this.props.type === 'situation' ? data.situation : data.rules,
          lastModificationTime: data.mtime,
          errorMessage: null
        })
      })
      .catch(e => this.handleError(e))
  }

  findAndReplaceTamediaDomains(data) {
    // iterate over every tenant and replace with current tenant
    Autotext.tenants.map(tenant => {
      const domain = tenant.domain + '.ch'
      const regex = new RegExp(RegExp.escape(domain), 'g')
      if (this.props.type === 'situation') {
        data.situation = data.situation.replace(
          regex,
          parseHostname().publicationDomain
        )
      } else if (this.props.type === 'rules') {
        data.rules = data.rules.replace(
          regex,
          parseHostname().publicationDomain
        )
      }
    })
    return data
  }

  /**
   * Retrieves processed texts from initial wordsmith templates
   * @param {string} kt Cantonal short name
   * @param {string} locale
   * @param {string} type
   */
  getCantonalText(kt, type, locale) {
    if (type !== 'situation' && type !== 'rules') {
      throw new Error('Type argument is not valid.')
    }
    const BASE_URL = config.apiBaseUrl
    const API_ENDPOINT =
      '/api/ccu/get_cantonal_' +
      (type === 'situation' ? 'situation/' : 'rules/')
    const endpoint = new URL(API_ENDPOINT + kt, BASE_URL)
    endpoint.searchParams.append('lang', locale)
    const url = endpoint

    return window
      .fetch(url.href, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      })
      .then(response => {
        if (response.ok) {
          return response.json()
        }
        throw new Error(response.status + ': ' + response.statusText)
      })
      .then(data => {
        // find all Tamedia domains and change to current hostname
        return this.findAndReplaceTamediaDomains(data)
      })
  }

  /**
   * @param {string} kt
   * @param {string} type
   * @param {string} locale
   */
  getLastModificationTime(kt, type, locale) {
    if (type !== 'situation' && type !== 'rules') {
      throw new Error('Type argument is not valid.')
    }
    const BASE_URL = config.apiBaseUrl
    const API_ENDPOINT = '/atm/ccu/get_last_modification_date/'
    const endpoint = new URL(API_ENDPOINT + kt, BASE_URL)
    endpoint.searchParams.append('lang', locale)
    endpoint.searchParams.append('type', type)
    const url = endpoint
    return window
      .fetch(url.href, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      })
      .then(response => {
        if (response.ok) {
          return response.json()
        }
        throw new Error(response.status + ': ' + response.statusText)
      })
  }

  /**
   * @param {{ message: any; }} error
   */
  handleError(error) {
    this.setState({ errorMessage: error.message })
    // console.group('Fetch autotext')
    // console.warn('Cantonal situation could not be loaded:')
    // console.warn(error)
    // console.groupEnd()
  }

  componentDidMount() {
    // console.log('autotext did mount')
    this.previousContext = this.context
  }

  /**
   * @param {{ kt: any; }} prevProps
   */
  componentDidUpdate(prevProps) {
    // console.log('autotext did update')
    // Typical usage (don't forget to compare props):
    if (
      this.props.kt !== prevProps.kt ||
      this.previousContext !== this.context
    ) {
      this.previousContext = this.context
      // get new autotext
      this.getCantonalText(this.props.kt, this.props.type, this.context)
        .then(data => {
          this.setState({
            autoText:
              this.props.type === 'situation' ? data.situation : data.rules,
            lastModificationTime: data.mtime,
            errorMessage: null
          })
        })
        .catch(e => this.handleError(e))
    }
  }

  render() {
    let lastModificationDate
    if (this.state.lastModificationTime) {
      const options = {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
      }
      lastModificationDate = new Date(
        1000 * parseFloat(this.state.lastModificationTime)
      )
      lastModificationDate = lastModificationDate.toLocaleString(
        'de-DE',
        options
      )
    } else {
      lastModificationDate = 0
    }
    const localization = STRINGS[this.context]
    const containerClasses = 'step article'
    // prepare markdown string if type is 'rules'
    let md
    if (this.state.autoText && this.props.type === 'rules') {
      // prepend a new line so markdown is rendered correctly
      md = '\n' + this.state.autoText
      // remove article ids
      md = md.replace(/___.*___/g, '')
    }

    // Setup content for the default case
    let Content = (
      <div className='autotext'>
        {/* situation is HTML, so should be rendered accordingly */}
        {this.state.autoText && this.props.type === 'situation' && (
          <>
            <div
              className='ta text'
              dangerouslySetInnerHTML={{
                __html: this.state.autoText ?? ''
              }}
            />
            {this.state.lastModificationTime && (
              <div className='ta updated'>
                {localization.lastModificationDate} {lastModificationDate}
              </div>
            )}
          </>
        )}
        {/* rules is MD, so should be rendered accordingly */}
        {this.state.autoText && this.props.type === 'rules' && (
          // add a newline before string, so MD is rendered correctly
          // TODO think of using these hints: https://github.com/probablyup/markdown-to-jsx/blob/master/README.md#getting-the-smallest-possible-bundle-size
          // compiler(md)
          <>
            <Markdown
              children={md}
              className='ta text'
              options={{
                overrides: {
                  a: {
                    component: TALink
                  }
                }
              }}
            />
            {this.state.lastModificationTime && (
              <div className='ta updated'>
                {localization.lastModificationDate} {lastModificationDate}
              </div>
            )}
          </>
        )}

        {!this.state.autoText && (
          <div className='ui placeholder'>
            {localization.contentLoadingMessage}
          </div>
        )}
      </div>
    )

    // show something else in case of an error
    if (this.state.errorMessage) {
      Content = (
        <div className='ui text container'>
          <FetchErrorMessage status={this.state.errorMessage ?? ''} />
        </div>
      )
    }

    return <div>{Content}</div>
  }
}
